import axios from 'axios';
import { API_URL } from '../config';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth services
export const registerUser = async (userData) => {
  try {
    const response = await api.post('/auth/direct-register', userData);
    return response.data;
  } catch (error) {
    console.error('Registration error:', error.response?.data || error.message);
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await api.post('/auth/direct-login', { phone: credentials.phone });
    return response.data;
  } catch (error) {
    console.error('Login error:', error.response?.data || error.message);
    throw error;
  }
};

// OTP-based auth services
export const requestOTP = async (phone) => {
  try {
    const response = await api.post('/auth/login', { phone });
    return response.data;
  } catch (error) {
    console.error('OTP request error:', error.response?.data || error.message);
    throw error;
  }
};

export const verifyLoginOTP = async (attempt, otp, program_code) => {
  try {
    // Get the phone number from localStorage or another source
    const loginData = JSON.parse(localStorage.getItem('loginAttempt') || '{}');
    const phone = loginData.phone || '';
    const program = program_code || '';

    console.log(phone, program);
    // First verify with the external API
    const verifyResponse = await api.post('/auth/verify-login', {
      attempt,
      otp,
      program
    });
    
    return verifyResponse.data;
  } catch (error) {
    console.error('OTP verification error:', error.response?.data || error.message);
    throw error;
  }
};

export const requestRegistrationOTP = async (userData) => {
  try {
    // Store the phone number for verification
    localStorage.setItem('registrationData', JSON.stringify({
      phone: userData.phone
    }));
    
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Registration OTP request error:', error.response?.data || error.message);
    throw error;
  }
};

export const verifyRegistrationOTP = async (registrationId, otp) => {
  try {
    // Get the phone number from localStorage or another source
    const registrationData = JSON.parse(localStorage.getItem('registrationData') || '{}');
    const phone = registrationData.phone || '';
    
    const response = await api.post('/auth/verify-registration', {
      registration_id: registrationId,
      otp,
      phone
    });
    
    return response.data;
  } catch (error) {
    console.error('Registration OTP verification error:', error.response?.data || error.message);
    throw error;
  }
};

// Program services
export const getUserPrograms = async (userId) => {
  try {
    // Attempt to get user's enrolled programs
    const response = await api.get(`/programs/user/${userId}/enrolled`);
    
    // If successful, return the data
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Fallback to mock data if the API returns empty array
    // if (response.data && response.data.length === 0) {
    //   return [
    //     {
    //       program_id: 1,
    //       name: 'IIT Mandi - Minor Program in Fullstack AI Engineering',
    //       institution: 'IIT Mandi',
    //       status: 'Qualifier Test Booking',
    //       test_date: '27th Sep',
    //     }
    //   ];
    // }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching user programs:', error.response?.data || error.message);
    
    // Return mock data as fallback
    return [
      {
        program_id: 1,
        name: 'IIT Mandi - Minor Program in Fullstack AI Engineering',
        institution: 'IIT Mandi',
        status: 'Qualifier Test Booking',
        test_date: '27th Sep',
      }
    ];
  }
};

export const getProgram = async (programId) => {
  try {
    const response = await api.get(`/programs/${programId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching program details:', error.response?.data || error.message);
    
    // Return mock data as fallback
    return {
      program_id: programId,
      name: 'IIT Mandi - Minor Program in Fullstack AI Engineering',
      institution: 'IIT Mandi'
    };
  }
};

// Test booking services
export const checkExistingBooking = async (userId, programId) => {
  try {
    const response = await api.get(`/tests/check-booking`, {
      params: {
        user_id: userId,
        program_id: programId,
      },
    });
    return response.data;
  } catch (error) {
    // If 404, it means no booking exists
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Error checking existing booking:', error.response?.data || error.message);
    throw error;
  }
};

export const getAvailableSlots = async (programId) => {
  // This endpoint is now commented out in the backend
  // We'll use the hardcoded slots in the BookSlot component
  return { available_slots: [] };
};

// Payment services
export const initiatePayment = async (paymentData) => {
  try {
    const response = await api.post('/payments/initiate', paymentData);
    return response.data;
  } catch (error) {
    console.error('Payment initiation error:', error.response?.data || error.message);
    throw error;
  }
};

export const getPaymentStatus = async (referenceNo) => {
  try {
    const response = await api.get(`/payments/status/${referenceNo}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment status:', error.response?.data || error.message);
    throw error;
  }
};

export const bookTestSlot = async (bookingData) => {
  try {
    // Initiate payment directly without creating a booking first
    // The booking will be created after successful payment
    const paymentData = {
      amount: bookingData.fee || 1, // Use the fee from booking data or fallback to 1
      user_id: bookingData.user_id,
      program_id: bookingData.program_id,
      test_date: bookingData.test_date,
      time_slot: bookingData.time_slot
    };
    
    // Initiate payment and get payment details
    const paymentResponse = await initiatePayment(paymentData);
    
    // Store the payment reference in sessionStorage instead of localStorage
    sessionStorage.setItem('current_payment_ref', paymentResponse.reference_no);
    
    // Redirect directly to the payment URL
    window.location.href = paymentResponse.payment_url;
    
    return {
      reference_no: paymentResponse.reference_no
    };
  } catch (error) {
    console.error('Error booking test slot:', error.response?.data || error.message);
    throw error;
  }
};

export const getBookingDetails = async (bookingId) => {
  try {
    const response = await api.get(`/tests/booking/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking details:', error.response?.data || error.message);
    throw error;
  }
};

// Counseling services
// export const getCounselingAvailableSlots = async (userId, testBookingId) => {
//   try {
//     const response = await api.get('/counseling/available-slots', {
//       params: {
//         user_id: userId,
//         test_booking_id: testBookingId
//       }
//     });
//     return response.data;
//   } catch (error) {
//     console.error('Error fetching counseling slots:', error.response?.data || error.message);
//     throw error;
//   }
// };

export const bookCounselingSlot = async (bookingData) => {
  try {
    console.log('API service: Sending booking data to server:', bookingData);
    const response = await api.post('/counseling/book', bookingData);
    console.log('API service: Received response from server:', response.data);
    return response.data;
  } catch (error) {
    console.error('API service: Error booking counseling slot:', error.response?.data || error.message);
    throw error;
  }
};

export const getCounselingBooking = async (bookingId) => {
  try {
    const response = await api.get(`/counseling/booking/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching counseling booking:', error.response?.data || error.message);
    throw error;
  }
};

export const getUserCounselingBookings = async (userId) => {
  try {
    const response = await api.get(`/counseling/bookings/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user counseling bookings:', error.response?.data || error.message);
    throw error;
  }
};

export const getCounselingByTest = async (testBookingId) => {
  try {
    const response = await api.get(`/counseling/test/${testBookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching counseling by test:', error.response?.data || error.message);
    throw error;
  }
};

export const getMeetingLink = async () => {
  try {
    const response = await api.get('/counseling/meeting-link');
    return response.data;
  } catch (error) {
    console.error('Error getting meeting link:', error.response?.data || error.message);
    throw error;
  }
};

// Qualifier Report services
export const getQualifierReport = async (bookingId) => {
  try {
    const response = await api.get(`/tests/qualifier-report/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting qualifier report:', error.response?.data || error.message);
    throw error;
  }
};

export default api;
